import {useEffect} from 'react'
import {toast} from 'react-toastify'
import {Link} from 'react-router-dom'
import {toastActionableConfig} from '@/shared/config/toastConfig'
import {getLogger} from '@/shared/utils'
import {registerViewerRun} from '../ai-chat/pendingRuns'
import type {JobLaunchedPayload} from './handler-structures'

/**
 * The page's one listener for `jobLaunched` — a viewer action wizard whose Finish submitted a job
 * instead of producing a result.
 *
 * Mounted once, like {@code FilePickerHost} and {@code ViewerNotificationHost}, and for the same
 * reason: several {@code GridViewerManager}s can be mounted at the same time, so a handler living in
 * one of them would fire once per manager and raise duplicate toasts.
 *
 * It also has to be here rather than in the viewer that launched the run, because a run outlives its
 * viewer: the tab can be closed, or the page reloaded, while the job is still going. The registry
 * this writes to is page-level and persisted for exactly that reason.
 *
 * <h4>Why this only records and announces</h4>
 * Nothing polls the run yet — there is no browser-reachable status API that covers both execution
 * lanes (WES ids and `mcp-` mini-job ids live in different stores). That design is written up in
 * docs/architecture/RUN_STATUS_API.md. Until it exists, a launched run is deliberately shown as
 * *submitted* rather than *running*, and the user is pointed at the Jobs page, so nothing promises
 * progress this page cannot observe.
 */
export function JobLaunchHost() {
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      const data = event.data
      // Anything can post to window: strings from browser extensions, nulls from dev tooling.
      if (typeof data !== 'object' || data === null) {
        return
      }
      if ((data as {action?: unknown}).action !== 'jobLaunched') {
        return
      }

      handleJobLaunched((data as {value?: JobLaunchedPayload}).value ?? {})
    }

    window.addEventListener('message', onMessage)

    return () => window.removeEventListener('message', onMessage)
  }, [])

  return null
}

function handleJobLaunched(payload: JobLaunchedPayload): void {
  const label = payload.label || payload.tool || 'Job'

  if (payload.error) {
    // A tool's own failure message, which the user generally has to read twice and paste into a bug
    // report — so it gets the longer fuse that hovering pauses. See {@link toastActionableConfig}.
    toast.error(`${label} could not be started: ${payload.error}`, toastActionableConfig)
    return
  }

  const runId = String(payload.runId ?? '').trim()
  if (!runId) {
    // Submitted, apparently, but with nothing to identify it by — so it cannot be tracked or even
    // named back to the user. Say something rather than swallow it, and log for diagnosis.
    getLogger().warn('jobLaunched carried no runId', payload)
    toast.warning(`${label} was submitted, but no run id came back.`, toastActionableConfig)
    return
  }

  registerViewerRun({runId, tool: payload.tool, state: payload.state, label: payload.label})

  // Its own id, so the link below dismisses this toast and not every other one on screen.
  const toastId = `job-launched-${runId}`
  toast.success(<JobSubmittedToast label={label} runId={runId} toastId={toastId} />, {
    ...toastActionableConfig,
    toastId,
  })
}

/**
 * Names the run and points at where it can actually be followed.
 *
 * A router `Link` rather than an anchor so it does not reload the app, and dismissing on click keeps
 * a stale notification from sitting over the page the user just navigated to.
 *
 * The body holds the two things the user came for — a run id to copy and a link to follow — so it
 * carries {@link toastActionableConfig}: neither survives a 3s auto-close, and `closeOnClick` would
 * tear the toast down as soon as a selection drag ends on the run id.
 */
function JobSubmittedToast({
  label,
  runId,
  toastId,
}: {
  label: string
  runId: string
  toastId: string
}) {
  return (
    <span>
      {label} submitted — run <code>{runId}</code>.{' '}
      <Link to='/jobs' onClick={() => toast.dismiss(toastId)}>
        Track it on the Jobs page
      </Link>
      .
    </span>
  )
}
